import { b } from "@/b";
import { AsyncButton } from "@/components/AsyncButton";
import {
  CURRENCY_FRAGMENT,
  CurrencyRenderer,
} from "@/components/CurrencyRenderer";
import { MEDIA_FRAGMENT, ResponsiveImage } from "@/components/ResponsiveImage";
import { Section } from "@/components/layout/Section";
import {
  Hook_IsSubscribedToVIP_Fragment,
  useGetManageSubscriptionUrl,
  useIsSubscribedToVIP,
} from "@/util/hook";
import { useMutation } from "@apollo/client";
import {
  Button,
  Flex,
  Grid,
  Heading,
  Box,
  Text,
  keyframes,
  UnorderedList,
  ListItem,
} from "@chakra-ui/react";
import { FragmentData } from "@gqlb/core";
import { useRouter } from "next/router";
import React, { useMemo } from "react";
import { Macros, Macros_Fragment } from "../product/Macros";
import { VIP_SUBSCRIPTION_SLUG } from "@/util/constants";
import {
  SubscriptionCurrencyRenderer,
  SubscriptionCurrency_Fragment,
} from "@/components/SubscriptionCurrencyRenderer";

const CartPreviewSection_Cart_Fragment = b.fragment(
  "CartPreviewSection_Cart",
  "Cart",
  (b) => [
    b.issuesPreventingCheckout((b) => [
      b.__on("CartGeneralIssue", (b) => [b.message()]),
      b.__on("CartItemIssue", (b) => [
        b.item((b) => [
          b.id(),
          b.__on("CartItemProduct", (b) => [
            b.id(),
            b.product((b) => [b.id(), b.name()]),
          ]),
        ]),
        b.message(),
      ]),
    ]),
    b.totalQuantity(),
    b.vipSavings((b) => [
      //
      b.__fragment(CURRENCY_FRAGMENT),
      b.value(),
    ]),
    b.messages((b) => [b.id(), b.message()]),
    b.items((b) => [
      //
      b.id(),
      b.__on("CartItemProduct", (b) => [
        //
        b.id(),
        b.quantity(),
        b.subtotal((b) => [b.__fragment(CURRENCY_FRAGMENT)]),
        b.selectionPaths((b) => [b.summary()]),
        b.macros((b) => [b.__fragment(Macros_Fragment)]),
        b.product((b) => [
          //
          b.id(),
          b.name(),
          b.images((b) => [b.__fragment(MEDIA_FRAGMENT)]),
        ]),
      ]),
      b.__on("CartItemSubscription", (b) => [
        b.id(),
        b.subscription((b) => [
          b.id(),
          b.name(),
          b.slug(),
          b.price((b) => [b.__fragment(SubscriptionCurrency_Fragment)]),
        ]),
      ]),
    ]),
  ]
);

export const CartPreviewSection_Fragment = b.fragment(
  "CartPreviewSection",
  "Query",
  (b) => [
    b.viewer((b) => [
      //
      b.id(),
      b.cart((b) => [b.__fragment(CartPreviewSection_Cart_Fragment)]),
    ]),
    b.__fragment(Hook_IsSubscribedToVIP_Fragment),
    b.subscription({ slug: VIP_SUBSCRIPTION_SLUG }, (b) => [
      b.id(),
      b.price((b) => [b.__fragment(SubscriptionCurrency_Fragment)]),
    ]),
  ]
);

const UpdateQuantity_Mutation = b.mutation(
  "UpdateQuantity",
  { item: "String!", quantity: "Int!" },
  (b, v) => [
    b.viewer((b) => [
      b.cart((b) => [
        b.item({ id: v.item }, (b) => [
          b.__on("CartItemProductMutation", (b) => [
            b.editQuantity({ quantity: v.quantity }, (b) => [
              b.__fragment(CartPreviewSection_Cart_Fragment),
            ]),
          ]),
        ]),
      ]),
    ]),
  ]
);

const AddVIP_Mutation = b.mutation("AddVIP", (b) => [
  b.viewer((b) => [
    b.cart((b) => [
      b.addSubscriptionItem({ slug: VIP_SUBSCRIPTION_SLUG }, (b) => [
        b.cart((b) => [b.__fragment(CartPreviewSection_Cart_Fragment)]),
      ]),
    ]),
  ]),
]);

const RemoveVIP_Mutation = b.mutation(
  "RemoveVIP",
  { itemId: "String!" },
  (b, v) => [
    b.viewer((b) => [
      b.cart((b) => [
        b.item({ id: v.itemId }, (b) => [
          b.remove((b) => [b.__fragment(CartPreviewSection_Cart_Fragment)]),
        ]),
      ]),
    ]),
  ]
);

const ClearMessage_Mutation = b.mutation(
  "ClearMessage",
  { id: "ID!" },
  (b, v) => [
    b.viewer((b) => [
      b.cart((b) => [
        b.clearMessage({ id: v.id }, (b) => [
          b.__fragment(CartPreviewSection_Cart_Fragment),
        ]),
      ]),
    ]),
  ]
);

export const CartSection: React.FC<{
  data: FragmentData<typeof CartPreviewSection_Fragment>;
}> = ({ data }) => {
  const router = useRouter();
  const newItemId =
    typeof router.query.new === "string" ? router.query.new : null;
  const { items, vipSavings } = data.viewer.cart;

  const productItems = useMemo(() => {
    return items.filter(
      (
        item
      ): item is Extract<typeof item, { __typename: "CartItemProduct" }> => {
        return item.__typename === "CartItemProduct";
      }
    );
  }, [items]);

  const [updateQuantity] = useMutation(UpdateQuantity_Mutation.document());
  const [removeVIP] = useMutation(RemoveVIP_Mutation.document());
  const [addVIP] = useMutation(AddVIP_Mutation.document());
  const [clearMessage] = useMutation(ClearMessage_Mutation.document());

  const cartHasVIPSubscription = useMemo(() => {
    return items.some(
      (i) =>
        i.__typename === "CartItemSubscription" &&
        i.subscription.slug === VIP_SUBSCRIPTION_SLUG
    );
  }, [items]);

  const viewerHasVIP = useIsSubscribedToVIP(data, false);
  const getManageSubscriptionUrl = useGetManageSubscriptionUrl();

  const show = keyframes`
    from { opacity: 0; transform: translateX(-100%); }
    to { opacity: 1; transform: translateX(0px); }
  `;

  let animationDelay = 0;

  function getShowAnimation() {
    animationDelay += 80;
    return `${show} 500ms cubic-bezier(.42,1.56,.53,.9) ${animationDelay}ms forwards`;
  }

  let vipBlock: React.ReactNode = null;

  if (viewerHasVIP) {
    vipBlock = (
      <Flex
        flexDir="column"
        animation={getShowAnimation()}
        opacity="0"
        mt="4"
        rounded="2xl"
        gridColumn="span 1"
      >
        <Text textTransform="uppercase" fontWeight="bold" fontSize="xl">
          You&apos;re a VIP!
        </Text>
        <Text mt="4" fontSize="sm">
          Today you&apos;ll save{" "}
        </Text>
        <Text as="span" fontWeight="bold" fontSize="2xl">
          <CurrencyRenderer data={vipSavings} />
        </Text>
        <AsyncButton
          size="sm"
          mt="auto"
          action={async () => {
            const url = await getManageSubscriptionUrl();
            if (!url) return;
            window.location.href = url;
          }}
        >
          Manage Subscription
        </AsyncButton>
      </Flex>
    );
  } else if (cartHasVIPSubscription) {
    vipBlock = (
      <Flex
        flexDir="column"
        animation={getShowAnimation()}
        opacity="0"
        mt="4"
        rounded="2xl"
        gridColumn="span 1"
      >
        <Text
          textTransform="uppercase"
          fontWeight="bold"
          fontSize={{
            base: "md",
            md: "xl",
          }}
        >
          VIP Subscription
        </Text>
        <Text mt="4" fontSize="sm">
          You are saving
        </Text>
        <Text as="span" fontWeight="bold" fontSize="2xl">
          <CurrencyRenderer data={vipSavings} />
        </Text>
        <Text fontSize="sm">now, and will save on all future orders!</Text>
        <Flex mt="auto" alignItems="center">
          <AsyncButton
            size={{
              base: "xs",
              md: "sm",
            }}
            action={async () => {
              const vipItem = items.find(
                (i) =>
                  i.__typename === "CartItemSubscription" &&
                  i.subscription.slug === VIP_SUBSCRIPTION_SLUG
              );
              if (!vipItem) {
                return;
              }
              await removeVIP({
                variables: {
                  itemId: vipItem.id,
                },
              });
            }}
          >
            Remove
          </AsyncButton>
          <Box
            ml="auto"
            color="#808080"
            fontSize={{
              base: "xs",
              md: "sm",
            }}
          >
            <SubscriptionCurrencyRenderer data={data.subscription!.price} />
          </Box>
        </Flex>
      </Flex>
    );
  } else if (vipSavings.value > 1999) {
    vipBlock = (
      <Flex
        flexDir="column"
        animation={getShowAnimation()}
        opacity="0"
        mt="4"
        rounded="2xl"
        gridColumn="span 2"
        rowGap="4"
      >
        <Text
          textTransform="uppercase"
          fontWeight="bold"
          fontSize={{
            base: "md",
            md: "xl",
          }}
        >
          You could save with VIP!
        </Text>
        <Text mt="4">
          Save{" "}
          <Text as="span" fontWeight="bold" fontSize="2xl">
            <CurrencyRenderer data={vipSavings} />
          </Text>{" "}
          now, and save on 25% all future orders!
        </Text>
        <UnorderedList>
          <ListItem>Save 25% on every meal</ListItem>
          <ListItem>Cancel your subscription at any time</ListItem>
          <ListItem>Enjoy all your favorites at a discount</ListItem>
        </UnorderedList>

        <Flex mt="auto" flexDir="row" alignItems="center">
          <AsyncButton
            size="sm"
            action={async () => {
              await addVIP();
            }}
            bg="#8DEBCF"
          >
            Add VIP to Cart
          </AsyncButton>
          <Box ml="auto" color="#808080">
            <SubscriptionCurrencyRenderer data={data.subscription!.price} />
          </Box>
        </Flex>
      </Flex>
    );
  }

  const checkoutIssues = data.viewer.cart.issuesPreventingCheckout;
  const messages = data.viewer.cart.messages;

  return (
    <Section padded>
      <Heading textTransform="uppercase" fontSize="4xl">
        Cart
      </Heading>
      {messages.length > 0 && (
        <Box my="4">
          <Heading as="h5" textTransform="uppercase" fontSize="lg">
            Messages
          </Heading>
          {messages.map((m) => (
            <Grid gridTemplateColumns="auto 1fr" columnGap="2" key={m.id}>
              <AsyncButton
                size="sm"
                buttonType="close"
                action={async () => {
                  await clearMessage({
                    variables: {
                      id: m.id,
                    },
                  });
                }}
                variant="ghost"
              />
              <Text key={m.id}>{m.message}</Text>
            </Grid>
          ))}
        </Box>
      )}
      <Grid
        gridTemplateColumns={{
          base: "1fr 1fr",
          md: "repeat(auto-fill, minmax(200px, 1fr))",
        }}
        gap="8"
      >
        {vipBlock}
        {productItems.map((item) => {
          const layers = Math.min(item.quantity, 4);

          return (
            <Flex
              key={item.id}
              flexDir="column"
              animation={getShowAnimation()}
              opacity="0"
              pos="relative"
            >
              {newItemId === item.id && (
                <Text
                  pos="absolute"
                  top="4"
                  left="4"
                  bg="#8DEBCF"
                  px="2"
                  py="1"
                  rounded="md"
                  fontSize="xs"
                  fontWeight="bold"
                  zIndex="99"
                >
                  Added to Cart!
                </Text>
              )}

              <Box
                pos="relative"
                w={`calc(100% - ${layers * 4}px)`}
                mt={`${layers * 4}px`}
                ml={`${layers * 4}px`}
                aspectRatio="1/1"
                data-group
              >
                {new Array(layers).fill(undefined).map((_, i) => (
                  <Box
                    key={i.toString()}
                    rounded="2xl"
                    overflow="hidden"
                    border="solid 2px #FFFFFF"
                    transform={`translate(${i * -4}px, ${i * -4}px)`}
                    transition="transform 500ms cubic-bezier(.42,1.56,.53,.9)"
                    _groupHover={{
                      transform: `translate(${i * -8}px, ${i * -8}px)`,
                    }}
                    pos="absolute"
                    inset="0"
                  >
                    <ResponsiveImage
                      image={item.product.images[0] as any}
                      width="full"
                      height="full"
                      objectFit="cover"
                    />
                  </Box>
                ))}
              </Box>
              {item.macros && (
                <Box
                  bottom="0"
                  p="2"
                  bg="whiteAlpha.800"
                  backdropFilter="blur(4px)"
                  rounded="lg"
                >
                  <Macros data={item.macros} size="sm" />
                </Box>
              )}
              <Text
                textAlign="left"
                textTransform="uppercase"
                fontSize="sm"
                fontWeight="bold"
                mt="2"
              >
                {item.product.name}
              </Text>
              <Box>
                {item.selectionPaths.map((path) => (
                  <Text
                    key={path.summary}
                    fontSize={{
                      base: "10px",
                      md: "xs",
                    }}
                    color="#808080"
                  >
                    {path.summary}
                  </Text>
                ))}
              </Box>
              <Flex flexDir="row" columnGap="2" my="1" mt="auto">
                <AsyncButton
                  size="xs"
                  action={async () => {
                    await updateQuantity({
                      variables: {
                        item: item.id,
                        quantity: item.quantity - 1,
                      },
                    });
                  }}
                >
                  -
                </AsyncButton>
                <Text w="6" textAlign="center">
                  {item.quantity}
                </Text>
                <AsyncButton
                  size="xs"
                  action={async () => {
                    await updateQuantity({
                      variables: {
                        item: item.id,
                        quantity: item.quantity + 1,
                      },
                    });
                  }}
                >
                  +
                </AsyncButton>
                <Box ml="auto" color="#808080">
                  <CurrencyRenderer data={item.subtotal} />
                </Box>
              </Flex>
            </Flex>
          );
        })}
        <Flex
          flexDir="column"
          animation={getShowAnimation()}
          opacity="0"
          mt="4"
          rounded="2xl"
          rowGap="4"
        >
          <Text
            textTransform="uppercase"
            fontWeight="bold"
            fontSize={{ base: "md", md: "xl" }}
          >
            Checkout
          </Text>
          {checkoutIssues.map((i) => (
            <Text
              key={i.message}
              fontSize={{ base: "xs", md: "sm" }}
              color="#6F7171"
            >
              {i.message}
            </Text>
          ))}
          {checkoutIssues.length === 0 && (
            <Text fontSize={{ base: "xs", md: "sm" }} color="#808080">
              You&apos;re ready to checkout!
            </Text>
          )}
          <Button
            mt="auto"
            size="sm"
            onClick={() => {
              router.push("/checkout");
            }}
            bg="#8DEBCF"
            isDisabled={data.viewer.cart.issuesPreventingCheckout.length > 0}
          >
            <Text
              as="span"
              display={{
                base: "none",
                lg: "inline",
              }}
            >
              Continue to Checkout {">"}
            </Text>
            <Text
              as="span"
              display={{
                base: "inline",
                lg: "none",
              }}
            >
              Checkout {">"}
            </Text>
          </Button>
        </Flex>
      </Grid>
    </Section>
  );
};
